import {AI_CHAT_PROMPT_BOX_ID} from '@/ai-chat/ai-chat.types';
import {getDirForMediaSizeType, getWriteBucket, MediaItemSizeType, repoURL} from './s3-library';
import {openAuthenticateModal} from '@Modals/authenticate-modal/authenticate-modal-library';
import {authenticateUser, getUserId, isUserLoggedIn, isUserNotLoggedIn, isUserPremium, isUserPremiumPlus, isUserTrial} from '@Libraries/user.library';
import type {BuyCreditsResponse} from '@/types/credits.types';
import type {UserImageVOResponse} from '@Libraries/user-image-library';
import {
  AiChatSource,
  ChatMessagePartType,
  RequestInstruction,
  type ChatMessageDesignPart,
  type ChatOnLoadParams,
  type FeedbackType,
  type ImagePart,
} from '@Components/ai-chat-window/ai-chat-window.types';
import type {AiPromptBoxAttachments, PromptAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {buildRequestPreferences, createUserRequestParts} from '@Components/ai-chat-window/lib/ai-chat-messages.library';
import {getAssetUrl} from '@Utils/s3.util';
import {getUserImageScreenBigUrl, getUserImageThumbUrl} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {AiChatTemplateAttachOrigin, GA4EventName} from '@Libraries/ga-events';
import type {UseAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {type AiPromptBox, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {isPAYGUser} from '@Utils/user.util';
import {noop} from '@Utils/general.util';
import {getPosterScreenImagePreviewURL, getPosterThumbnailImagePreviewURL} from '@Libraries/poster-library';
import {updateAiChatIsPremiumOnly, updateAiChatMinimumTurnCredits} from '@/ai-chat/ai-chat.reducer';
import type {DeepPartial} from '@/global';
import debounce from 'lodash/debounce';
import type {AIChatNegativeFeedbackTypes} from '@Components/ai-chat-window/components/feedback-popup/feedback-popup.types';
import {goToMyStuff} from '@Libraries/my-stuff/my-stuff-library';
import {refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {getSessionStorageItem, removeSessionStorageItem, setSessionStorageItem} from '@Utils/session-storage.util';

const EXTENSION = 'png';

export enum AiChatCostType {
  UPSCALE_IMAGE_COST = 1,
  ANIMATION_WITHOUT_AUDIO_COST = 7,
  ANIMATION_WITH_AUDIO_COST = 10,
  // CodeReviewZainLater: why silence warning here? please handle;
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  MAKE_TEXT_EDITABLE_COST = 1,
}

/** The backend's own minimum, so this gate can never drift from CostToCredits. */
export const getAiChatMessageCost = async (): Promise<number> => {
  const cachedValue = getAiChatMessageCostFromStore();

  if (cachedValue !== null) {
    return cachedValue;
  }

  const minimumTurnCredits = (await window.PMW.readLocal('ai/minimumTurnCredits')) as number;

  window.PMW.redux.store.dispatch(
    updateAiChatMinimumTurnCredits({
      minimumTurnCredits,
    })
  );

  return minimumTurnCredits;
};

export const getAiChatMessageCostFromStore = (): number | null => {
  return window.PMW.redux.store.getState().aiChat.minimumTurnCredits;
};

interface ModelImageAssetUploadParams {
  userId: number | undefined;
  imageHashedId: string;
  tempUploadingImageUID: string;
}

async function getURLForDesignPart(part: ChatMessageDesignPart, useThumbSize: boolean): Promise<string> {
  if (useThumbSize) {
    return await getPosterThumbnailImagePreviewURL(part.hashedId, part.pageHashedId);
  }
  return await getPosterScreenImagePreviewURL(part.hashedId, part.pageHashedId);
}

export const getUrlForImagePart = async (part: ImagePart, useThumbSize = false): Promise<string> => {
  switch (part.type) {
    case ChatMessagePartType.MODEL_GENERATED_IMAGE:
    case ChatMessagePartType.REATTACHED_MODEL_IMAGE:
      return getUrlForGeneratedAiChatModelAssetsImage(part.hashedId, useThumbSize ? MediaItemSizeType.THUMB : MediaItemSizeType.HIGH_RES);
    case ChatMessagePartType.EXAMPLE_IMAGE_ASSET:
      return getAssetUrl(`${part.fileName}${useThumbSize ? '-thumb' : ''}`);
    case ChatMessagePartType.DESIGN:
      return getURLForDesignPart(part, useThumbSize);
    case ChatMessagePartType.USER_IMAGE:
      if (useThumbSize) {
        return getUserImageThumbUrl(part.hashedFilename, part.extension);
      }
      return getUserImageScreenBigUrl(part.hashedFilename);
    case ChatMessagePartType.IMAGE_GRAPHIC_ITEM_SNAPSHOT:
      return part.url;
    default:
      throw new Error(`Unhandled case for getUrlForImagePart`);
  }
};

export const getUniqueKeyForImagePart = (part: ImagePart): string => {
  switch (part.type) {
    case ChatMessagePartType.MODEL_GENERATED_IMAGE:
      return `${ChatMessagePartType.MODEL_GENERATED_IMAGE}-${part.hashedId}`;
    case ChatMessagePartType.EXAMPLE_IMAGE_ASSET:
      return `${ChatMessagePartType.EXAMPLE_IMAGE_ASSET}-${part.fileName}`;
    case ChatMessagePartType.DESIGN:
      return `${ChatMessagePartType.DESIGN}-${part.hashedId}${part.pageHashedId ? `-${part.pageHashedId}` : ''}`;
    case ChatMessagePartType.USER_IMAGE:
      return `${ChatMessagePartType.USER_IMAGE}-${part.hashedFilename}.${part.extension}`;
    case ChatMessagePartType.IMAGE_GRAPHIC_ITEM_SNAPSHOT:
      return `${ChatMessagePartType.IMAGE_GRAPHIC_ITEM_SNAPSHOT}-${part.url}`;
    case ChatMessagePartType.REATTACHED_MODEL_IMAGE:
      return `${ChatMessagePartType.REATTACHED_MODEL_IMAGE}-${part.hashedId}`;
    default:
      throw new Error(`Unhandled case for getUniqueKeyForImagePart`);
  }
};

export const getAltTextForImagePart = (part: ImagePart): string => {
  switch (part.type) {
    case ChatMessagePartType.MODEL_GENERATED_IMAGE:
      return window.i18next.t('pmwjs_generated_image');
    case ChatMessagePartType.EXAMPLE_IMAGE_ASSET:
    case ChatMessagePartType.DESIGN:
    case ChatMessagePartType.USER_IMAGE:
    case ChatMessagePartType.IMAGE_GRAPHIC_ITEM_SNAPSHOT:
    case ChatMessagePartType.REATTACHED_MODEL_IMAGE:
      return window.i18next.t('pmwjs_image');
    default:
      throw new Error(`Unhandled case for getAltTextForImagePart`);
  }
};

export const getUrlForGeneratedAiChatModelAssetsImage = (hashedId: string, sizeType: MediaItemSizeType): string => {
  return repoURL(`${getDirPathForAiChatModelAssetsImage(sizeType)}/${hashedId}.${EXTENSION}`, getWriteBucket());
};

/**
 * Resolve a stream-component media value to a displayable URL: a full URL (http…) is
 * used as-is; a bare AI-chat asset hashedId is converted to its asset URL. Lets
 * components accept either form, including older drafts persisted with just a hashedId.
 */
export const resolveChatMediaUrl = (value: string, sizeType: MediaItemSizeType): string => {
  if (value.startsWith('http')) {
    return value;
  }
  return getUrlForGeneratedAiChatModelAssetsImage(value, sizeType);
};

export const getUrlForAiChatAnimationThumbnail = (animationHashedId: string): string => {
  return repoURL(`aichatmodelassets/animation_thumbs/${animationHashedId}.jpg`, getWriteBucket());
};

export const getUrlForAiChatAnimation = (animationHashedId: string): string => {
  return repoURL(`aichatmodelassets/animations/${animationHashedId}.mp4`, getWriteBucket());
};

const getDirPathForAiChatModelAssetsImage = (sizeType: MediaItemSizeType): string => {
  const BASE_DIR = 'aichatmodelassets';
  const subDirForSize = getDirForMediaSizeType(sizeType);
  return `${BASE_DIR}${subDirForSize ? `/${subDirForSize}` : ''}`;
};

export const openAiChatAuthenticateModal = (onLoginSucess: VoidFunction): Promise<boolean> => {
  return new Promise((resolve) => {
    openAuthenticateModal({
      fbPermissions: '',
      signupSource: 'ai-chat',
      panelSubtext: window.i18next.t('pmwjs_ai_chat_auth_modal_subtext'),
      onLoginSuccess: async (): Promise<void> => {
        onLoginSucess();
        resolve(true);
        return Promise.resolve();
      },
      onCloseCb() {
        resolve(false);
      },
    });
  });
};

export const authenticateUserForAiChat = async (cb: () => void | Promise<void>, onCloseCb = noop): Promise<void> => {
  await authenticateUser(cb, onCloseCb, {
    panelSubtext: window.i18next.t('pmwjs_ai_chat_auth_modal_subtext'),
  });
};

export const showAiChatPremiumOnlyFeatureDialog = (hasChatHistory: boolean, aiPromptBoxState?: AiPromptBox, extraAttachments: PromptAttachment[] = []): void => {
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_NO_CREDITS);

  if (aiPromptBoxState !== undefined) {
    stashPendingAiChatSubmitFromState(aiPromptBoxState, extraAttachments);
  }

  if (hasChatHistory) {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_CHAT_KEEP_CREATING as string);
    return;
  }

  void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_CHAT_CREATE_MORE as string);
};

const stashPendingAiChatSubmitFromState = (aiPromptBoxState: AiPromptBox, extraAttachments: PromptAttachment[]): void => {
  stashPendingAiChatSubmit({
    prompt: aiPromptBoxState.prompt,
    attachments: getConsolidatedAttachmentsForAiChat(aiPromptBoxState, extraAttachments),
    source: aiPromptBoxState.source,
    pinnedAspectRatio: aiPromptBoxState.pinnedAspectRatio,
  });
};

const getCreditsDialogueHeadingText = (): string => {
  if (isUserPremiumPlus()) {
    return window.i18next.t('pmwjs_continue_using_ai_features_purchase_more_credits_or_upgrade_premium_plus');
  }

  if (isUserPremium()) {
    return window.i18next.t('pmwjs_continue_using_ai_fearures_purchase_more_credits');
  }

  throw new Error(`Unhandled case for PAYG and ANON users`);
};

export const ensureAiChatCreditsForCost = async (cost: number): Promise<boolean> => {
  const {credits} = await refreshTeamCreditsInStore();
  if (credits < cost) {
    const purchaseData = await showPurchaseCreditsDialog();
    if (!purchaseData?.creditsV2 || purchaseData.creditsV2 < cost) {
      return false;
    }
  }
  return true;
};

export const showPurchaseCreditsDialog = (): Promise<BuyCreditsResponse | null> => {
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_CREDITS_FINISHED);

  return new Promise((resolve) => {
    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
      window.PMW.stripeCheckout.showStripeCheckoutForCreditPackPurchase(
        (data: BuyCreditsResponse) => {
          resolve(data);
        },
        () => {
          resolve(null);
        },
        getCreditsDialogueHeadingText(),
        window.i18next.t('pmwjs_youre_out_of_credits')
      );
    });
  });
};

const aiModelAssetUpload = async (p: ModelImageAssetUploadParams): Promise<UserImageVOResponse> => {
  return (await window.PMW.writeLocal('ai/uploadModelImageAsset', p)) as UserImageVOResponse;
};

const checkGeneratedImageUploadStatus = async (imageHashedId: string): Promise<void> => {
  await window.PMW.readLocal('ai/getModelImageAssetUploadStatus', {
    imageHashedId: imageHashedId,
  });
};

export const uploadModelAsset = async (imageHashedId: string, tempUploadingImageUID: string): Promise<void> => {
  await window.PMW.pollingAjaxCallAsync(
    aiModelAssetUpload.bind(null, {
      userId: getUserId(),
      imageHashedId: imageHashedId,
      tempUploadingImageUID,
    }),
    checkGeneratedImageUploadStatus.bind(null, imageHashedId)
  );
};

const getRequestInstruction = (source?: AiChatSource): RequestInstruction | undefined => {
  if (source === AiChatSource.GALLERY_PREVIEW_DIALOG || source === AiChatSource.ART_TEMPLATE_PAGE) {
    return RequestInstruction.INJECT_GALLERY_PREVIEW_DIALOG_PROMPT;
  }
  return undefined;
};

interface InitiateAiChatOnLoadParams {
  prompt: string;
  attachments: AiPromptBoxAttachments['items'];
  openInNewTab?: boolean;
  source?: AiChatSource;
  pinnedAspectRatio: string | null;
}

export const initiateAiChatOnLoad = ({prompt, attachments, openInNewTab = true, source, pinnedAspectRatio}: InitiateAiChatOnLoadParams): void => {
  const userRequestParts = createUserRequestParts(prompt, attachments);
  const chatOnLoadParams: ChatOnLoadParams = {
    userRequestParts: userRequestParts,
    source: source,
    requestInstruction: getRequestInstruction(source),
    sendOnLoad: true,
    preferences: buildRequestPreferences(pinnedAspectRatio),
  };

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = window.PMW.util.site_url('ai/chat');
  form.target = openInNewTab ? '_blank' : '_self';

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'chatOnLoadParams';
  input.value = JSON.stringify(chatOnLoadParams);
  form.appendChild(input);

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
  form.removeChild(input);
};

export interface InitiateAiChatViaGetParams {
  prompt?: string;
  source?: AiChatSource;
  designHashedId?: string;
  pageHashedId?: string;
  includeAllPages?: boolean;
  sendOnLoad?: boolean;
  openInNewTab?: boolean;
}

export const initiateAiChatOnLoadViaGet = ({prompt, source, designHashedId, pageHashedId, includeAllPages, sendOnLoad, openInNewTab = true}: InitiateAiChatViaGetParams): void => {
  const params = new URLSearchParams();

  if (prompt) {
    params.set('prompt', prompt);
  }

  if (source) {
    params.set('source', source);
  }

  if (designHashedId) {
    params.set('designHashedId', designHashedId);
  }

  if (includeAllPages) {
    params.set('includeAllPages', '1');
  } else if (pageHashedId) {
    params.set('pageHashedId', pageHashedId);
  }

  if (sendOnLoad) {
    params.set('sendOnLoad', '1');
  }

  const queryString = params.toString();
  const url = window.PMW.util.site_url(`ai/chat${queryString ? `?${queryString}` : ''}`);
  window.open(url, openInNewTab ? '_blank' : '_self');
};

const goToAiChatHome = (): void => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = window.PMW.util.site_url('ai/chat');
  form.target = '_self';

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

const goToAiChatHomeWithDelay = (timeoutDelay = 1000): void => {
  setTimeout(() => {
    goToAiChatHome();
  }, timeoutDelay);
};

export const handleConnectionIssue = (): void => {
  window.location.reload();
};

export const handleChatNotFound = (): void => {
  showMessageGrowl({
    type: GROWL_TYPE.DANGER,
    text: window.i18next.t('pmwjs_chat_not_found'),
    showCloseIcon: false,
    interval: 1000,
  });

  goToAiChatHomeWithDelay();
};

export const recordFeedback = async (
  chatId: string,
  messageId: string,
  feedbackType: FeedbackType,
  negativeFeedbackPreset: AIChatNegativeFeedbackTypes | null,
  comment: string | null
): Promise<void> => {
  await window.PMW.writeLocal('ai/recordAiChatMessageFeedback', {
    chatId,
    messageId,
    feedbackType,
    comment,
    negativeFeedbackPreset,
  });
};

export const hasUserEverCreatedAiChat = async (): Promise<boolean> => {
  const response = (await window.PMW.readLocal('ai/hasUserEverCreatedAiChat')) as {hasCreatedChat: boolean};
  return response.hasCreatedChat;
};

export const showConvertTrialForAiChat = (onSuccess: () => Promise<void> | void): void => {
  window.PMW.premiumDialogs.openConvertTrialDialog(
    'buy-credits',
    () => {
      void Promise.resolve(onSuccess()).catch(noop);
    },
    noop,
    noop
  );
};

const PENDING_AI_CHAT_SUBMIT_KEY = 'pmwPendingAiChatSubmit';

interface PendingAiChatSubmit {
  prompt: string;
  attachments: PromptAttachment[];
  source?: AiChatSource;
  pinnedAspectRatio?: string | null;
}

const stashPendingAiChatSubmit = (data: PendingAiChatSubmit): void => {
  setSessionStorageItem(PENDING_AI_CHAT_SUBMIT_KEY, data);
};

const readAndClearPendingAiChatSubmit = (): PendingAiChatSubmit | null => {
  const data = getSessionStorageItem(PENDING_AI_CHAT_SUBMIT_KEY) as PendingAiChatSubmit | null;
  if (data === null) {
    return null;
  }
  removeSessionStorageItem(PENDING_AI_CHAT_SUBMIT_KEY);
  return data;
};

export const getAiPromptBoxStateFromStore = (id: string): AiPromptBox | undefined => {
  return window.PMW.redux.store.getState().aiPromptBox[id];
};

export const submitPendingAiChatPromptIfReady = async (): Promise<void> => {
  if (!isUserPremium() && !isUserPremiumPlus() && !isUserTrial()) {
    return;
  }
  const pending = readAndClearPendingAiChatSubmit();
  if (pending === null) {
    return;
  }
  const {credits} = await refreshTeamCreditsInStore();
  if (credits < (await getAiChatMessageCost())) {
    return;
  }
  initiateAiChatOnLoad({prompt: pending.prompt, attachments: pending.attachments, openInNewTab: false, source: pending.source, pinnedAspectRatio: pending.pinnedAspectRatio ?? null});
};

/**
 * Runs the premium-only and credit/PAYG gate for starting an AI chat generation. Shows the appropriate
 * blocking dialog and returns false when the user cannot proceed, so callers can gate before navigating.
 */
export const gateAiChatGeneration = async (aiPromptBox: UseAiPromptBox, fetchedCredits: number | null | undefined, extraAttachments: PromptAttachment[] = []): Promise<boolean> => {
  const credits = fetchedCredits ?? 0;
  const {actions: promptBoxActions, state: aiPromptBoxState} = aiPromptBox;

  promptBoxActions.update({
    promptBoxState: PromptBoxState.LOADING,
  });

  const isAiChatPremiumOnly = await getIsAiChatPremiumOnly();

  if (isAiChatPremiumOnly && !isUserPremium()) {
    const hasChatHistory = await hasUserEverCreatedAiChat();
    showAiChatPremiumOnlyFeatureDialog(hasChatHistory, aiPromptBoxState, extraAttachments);
    promptBoxActions.update({
      promptBoxState: PromptBoxState.IDLE,
    });
    return false;
  }

  if (credits < (await getAiChatMessageCost())) {
    if (isUserTrial()) {
      showConvertTrialForAiChat(async () => {
        await handleOutOfCreditsInPromptBox(aiPromptBoxState, extraAttachments);
      });
    } else {
      await handleOutOfCreditsInPromptBox(aiPromptBoxState, extraAttachments);
    }

    promptBoxActions.update({
      promptBoxState: PromptBoxState.IDLE,
    });

    return false;
  }

  return true;
};

export const onAiPromptBoxSubmit = async (aiPromptBox: UseAiPromptBox, fetchedCredits: number | null | undefined, extraAttachments: PromptAttachment[] = []): Promise<void> => {
  if (!(await gateAiChatGeneration(aiPromptBox, fetchedCredits, extraAttachments))) {
    return;
  }

  const {state: aiPromptBoxState} = aiPromptBox;
  initiateAiChatOnLoad({
    prompt: aiPromptBoxState.prompt,
    attachments: getConsolidatedAttachmentsForAiChat(aiPromptBoxState, extraAttachments),
    openInNewTab: false,
    source: aiPromptBoxState.source,
    pinnedAspectRatio: aiPromptBoxState.pinnedAspectRatio ?? null,
  });
};

const handleOutOfCreditsInPromptBox = async (aiPromptBoxState: AiPromptBox, extraAttachments: PromptAttachment[]): Promise<void> => {
  if (isPAYGUser()) {
    const hasChatHistory = await hasUserEverCreatedAiChat();
    showAiChatPremiumOnlyFeatureDialog(hasChatHistory, aiPromptBoxState, extraAttachments);
    return;
  }

  const purchaseData = await showPurchaseCreditsDialog();

  if (!purchaseData?.creditsV2) {
    return;
  }

  if (purchaseData.creditsV2 >= (await getAiChatMessageCost())) {
    initiateAiChatOnLoad({
      prompt: aiPromptBoxState.prompt,
      attachments: getConsolidatedAttachmentsForAiChat(aiPromptBoxState, extraAttachments),
      openInNewTab: false,
      pinnedAspectRatio: aiPromptBoxState.pinnedAspectRatio ?? null,
    });
  }
};

export const getConsolidatedAttachmentsForAiChat = (aiPromptBoxState: AiPromptBox, extraAttachments: PromptAttachment[]): PromptAttachment[] => {
  return [...extraAttachments, ...(aiPromptBoxState.attachments?.items ?? [])];
};

export const checkUserImagePresets = async (imageHashedIds: string[]): Promise<Record<string, boolean>> => {
  return (await window.PMW.readLocal('ai/checkUserImagePresets', {imageHashedIds})) as Record<string, boolean>;
};

export const getIsAiChatPremiumOnly = async (): Promise<boolean> => {
  const cachedValue = getIsAiChatPremiumOnlyFromStore();

  if (cachedValue !== null) {
    return cachedValue;
  }

  const isPremiumOnly = (await window.PMW.readLocal('ai/isAiChatPremiumOnly')) as boolean;

  window.PMW.redux.store.dispatch(
    updateAiChatIsPremiumOnly({
      isPremiumOnly,
    })
  );

  return isPremiumOnly;
};

export const getIsAiChatPremiumOnlyFromStore = (): boolean | null => {
  return window.PMW.redux.store.getState().aiChat.isPremiumOnly;
};

export const renameChat = (chatId: string, newTitle: string): Promise<void> => {
  return window.PMW.writeLocal('ai/renameChat', {
    chatId,
    newTitle,
  });
};

export const deleteChat = (chatId: string): Promise<void> => {
  return window.PMW.writeLocal('ai/deleteChat', {chatId});
};

export const checkDailyLimit = debounce(
  async (promptBoxUpdateAction: (options: DeepPartial<AiPromptBox>) => void): Promise<void> => {
    if (isUserNotLoggedIn()) {
      return;
    }

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = (await window.PMW.readLocal('ai/checkDailyImageLimit', {timezone})) as boolean;
      if (response) {
        promptBoxUpdateAction({isDailyLimitReached: true});
      } else {
        promptBoxUpdateAction({isDailyLimitReached: false});
      }
    } catch {
      /* empty */
    }
  },
  60000,
  {leading: true, trailing: false}
);

export const getAiChatPromptBoxId = (chatId?: string): string => {
  return chatId ? `${AI_CHAT_PROMPT_BOX_ID}_${chatId}` : AI_CHAT_PROMPT_BOX_ID;
};

export const onLogoClick = (): void => {
  if (isUserLoggedIn()) {
    goToMyStuff();
  } else {
    window.location.href = window.PMW.util.site_url('');
  }
};

let lastTemplateAttachOrigin: AiChatTemplateAttachOrigin = AiChatTemplateAttachOrigin.PROMPTBOX_ATTACHMENTS;

export const openAttachTemplateGalleryDialog = (origin?: AiChatTemplateAttachOrigin, searchTerm?: string): void => {
  if (origin) {
    lastTemplateAttachOrigin = origin;
  }
  const galleryPage = searchTerm ? `posters/search?s=${encodeURIComponent(searchTerm)}` : '';
  window.PMW.openGalleryDialog(
    '',
    true,
    galleryPage,
    {showEventsWizardBanner: false, shouldUpsellForSemanticTagging: false},
    false,
    true,
    window.i18next.t('pmwjs_select_a_template'),
    'attach'
  );
};

export const getLastTemplateAttachOrigin = (): AiChatTemplateAttachOrigin => lastTemplateAttachOrigin;
